import React from "react";
import Cookies from 'js-cookie';
import logo from "../../data/logo/logo.png";
import { useStateContext } from '../../contexts/ContextProvider';
import { FiLogOut, FiUser, FiClock, FiCheckCircle, FiXCircle, FiInfo, FiMapPin, FiUsers, FiHelpCircle, FiMail } from "react-icons/fi";

const Completed = ({ history }) => {
  const { user, school } = useStateContext();

  const logoutHandler = () => {
    Cookies.remove('accessToken');
    localStorage.removeItem("tokenWithUser");
    history.push("/signin");
  };

  const getStatusConfig = (status) => {
    switch (status) {
      case 'approved':
        return {
          icon: <FiCheckCircle className="w-16 h-16 text-green-500" />,
          title: "Account Approved",
          description: "Your account has been verified and approved. You can now access the full dashboard.",
          color: "text-green-700 bg-green-50 border-green-200",
          badge: "bg-green-100 text-green-800"
        };
      case 'rejected':
        return {
          icon: <FiXCircle className="w-16 h-16 text-red-500" />,
          title: "Account Rejected",
          description: "There represent issues with your submission. Please contact support for assistance.",
          color: "text-red-700 bg-red-50 border-red-200",
          badge: "bg-red-100 text-red-800"
        };
      default:
        return {
          icon: <FiClock className="w-16 h-16 text-yellow-500" />,
          title: "Under Review",
          description: "Your account is currently undergoing a human review process. We will get back to you within 2-6 hours.",
          color: "text-yellow-700 bg-yellow-50 border-yellow-200",
          badge: "bg-yellow-100 text-yellow-800"
        };
    }
  };

  const statusConfig = getStatusConfig(school?.kyc_status);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex flex-col">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <div className="flex-shrink-0 flex items-center">
              <img className="h-8 w-auto" src={logo} alt="Logo" />
            </div>
            <div className="flex items-center space-x-4">
              <div className="hidden sm:flex items-center space-x-2 text-gray-700 dark:text-gray-200">
                <div className="w-8 h-8 rounded-full bg-indigo-100 dark:bg-indigo-900 flex items-center justify-center text-indigo-600 dark:text-indigo-300">
                  <FiUser className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium">{user?.email}</span>
              </div>
              <button
                onClick={logoutHandler}
                className="p-2 rounded-full text-gray-500 hover:text-red-600 hover:bg-red-50 transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                title="Logout"
              >
                <FiLogOut className="w-5 h-5" />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 py-8 max-w-3xl">
        <div className="bg-white dark:bg-gray-800 rounded-3xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">

          {/* Status Hero */}
          <div className="px-8 py-10 flex flex-col items-center text-center border-b border-gray-100 dark:border-gray-700">
            <div className={`p-4 rounded-full ${statusConfig.badge} bg-opacity-20 mb-6 animate-pulse`}>
              {statusConfig.icon}
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white mb-3">
              {statusConfig.title}
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-lg mx-auto text-lg leading-relaxed">
              {statusConfig.description}
            </p>

            <div className={`mt-6 px-4 py-2 rounded-full text-sm font-bold uppercase tracking-wide ${statusConfig.badge}`}>
              Status: {school?.kyc_status || 'Pending'}
            </div>
          </div>

          {/* Details List */}
          <div className="px-6 py-6 bg-gray-50 dark:bg-gray-800/50">
            <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-4 px-2">
              Submission Details
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center">
                    <FiInfo className="w-5 h-5" />
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">School Information</span>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                  Under Review
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-purple-100 text-purple-600 flex items-center justify-center">
                    <FiMapPin className="w-5 h-5" />
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Address Information</span>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                  Under Review
                </span>
              </div>

              <div className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm">
                <div className="flex items-center space-x-4">
                  <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-600 flex items-center justify-center">
                    <FiUsers className="w-5 h-5" />
                  </div>
                  <span className="font-medium text-gray-900 dark:text-white">Owners & Directors</span>
                </div>
                <span className="px-3 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                  Under Review
                </span>
              </div>
            </div>
          </div>

          {/* Footer Help */}
          <div className="bg-indigo-50 dark:bg-gray-900 px-8 py-6 flex items-center justify-between border-t border-indigo-100 dark:border-gray-700">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-white dark:bg-gray-800 rounded-full shadow-sm text-indigo-600">
                <FiHelpCircle className="w-6 h-6" />
              </div>
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">Need help?</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">Our support team is allowed to assist you.</p>
              </div>
            </div>
            <a href="mailto:support@acceede.com" className="flex items-center space-x-2 text-indigo-600 hover:text-indigo-800 font-medium transition-colors">
              <FiMail className="w-5 h-5" />
              <span>support@acceede.com</span>
            </a>
          </div>

        </div>
      </main>
    </div>
  );
};

export default Completed;